import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Calculator, TrendingUp, AlertTriangle, Wallet, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import CareerTargetLock from '../components/CareerTargetLock';
import KnownFactsStrip from '../components/KnownFactsStrip';
import { useFPContext } from '../context/FuturePathContext';
import { TrendingUp as A1, Wallet as A2, Clock as A3, Scale as A4, GraduationCap as A5, AlertTriangle as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api/learning-roi`;

const BAND = {
  strong: { hue: '#34d399', label: 'Strong' },
  good: { hue: '#22d3ee', label: 'Worth it' },
  marginal: { hue: '#fbbf24', label: 'Marginal' },
  no: { hue: '#f87171', label: "Doesn't clear" },
};

const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">{label}</label>
    {children}
  </div>
);

export default function LearningRoiPage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader, user } = useAuth();
  const { mayAsk, askInstead, askReason } = useFPContext();
  const [types, setTypes] = useState([]);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    credential_type: 'short_certificate', target_career: '', cost: '', months: '',
    current_income: '', funding_covers: '', borrowing: '', keep_working: true,
  });

  const run = useCallback(async (payload) => {
    setBusy(true);
    try {
      const res = await axios.post(API, payload, getAuthHeader());
      setD(res.data);
    } catch { toast.error('Could not run that calculation.'); }
    finally { setBusy(false); setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => {
    (async () => {
      const o = await axios.get(`${API}/options`, getAuthHeader());
      setTypes(o.data.credential_types);
      await run({ credential_type: 'short_certificate' });
    })();
  }, [getAuthHeader, run]);

  const calculate = () => run({
    credential_type: form.credential_type,
    target_career: form.target_career || undefined,
    cost: form.cost !== '' ? Number(form.cost) : undefined,
    months: form.months !== '' ? Number(form.months) : undefined,
    current_income: form.current_income !== '' ? Number(form.current_income) : undefined,
    funding_covers: form.funding_covers !== '' ? Number(form.funding_covers) : undefined,
    borrowing: form.borrowing !== '' ? Number(form.borrowing) : undefined,
    keep_working: form.keep_working,
  });

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  const chosen = types.find(t => t.key === form.credential_type);
  const band = BAND[d?.band] || BAND.marginal;
  const payback = d?.return?.payback_years;
  const paybackPct = payback ? Math.max(6, Math.min(100, Math.round((1 / payback) * 100))) : 0;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="roi-arrival"
        eyebrow="Learning ROI"
        title="Is this worth what it costs?"
        subtitle="Every credential is an investment. This one gets treated like one: cost in, wage lift out, how long until it pays for itself, and whether a cheaper route gets you the same job."
        accent="#22d3ee"
        second="#34d399"
        ctaLabel="Test the return"
        narration={"This is Learning ROI. Education is sold on hope, so we treat it as an investment instead. You put in what the programme costs and what it leads to, and we work out the wage lift, the payback period, and what you give up while you study. Then we check whether a shorter or cheaper route reaches the same job, because often it does, and nobody says so."}
        facts={[
          { icon: A2, head: "Cost in, wage out", sub: "Both sides of the trade, in real numbers" },
          { icon: A3, head: "Payback period", sub: "How long until it pays for itself" },
          { icon: A4, head: "Cheaper routes", sub: "Whether something shorter reaches the same job" }
        ]}
        modules={[
          { icon: A5, label: "The credential", line: "What it is, what it takes, what it costs", hue: "#22d3ee" },
          { icon: A1, label: "Wage lift", line: "Before and after, from federal wage data", hue: "#34d399" },
          { icon: A3, label: "Break-even", line: "The year it turns positive", hue: "#fbbf24" },
          { icon: A2, label: "Opportunity cost", line: "Earnings given up while studying", hue: "#f472b6" },
          { icon: A4, label: "Alternatives", line: "Shorter, cheaper or funded routes to the same role", hue: "#a78bfa" },
          { icon: A6, label: "When not to", line: "The cases where the honest answer is no", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <PageHeader
        title="Learning ROI"
        description="Does this credential pay for itself, and when? Real wages, real costs, real interest."
      />

      <CareerTargetLock className="mb-4" />
      <KnownFactsStrip className="mb-4" keys={['state', 'education_stage', 'employment_status', 'income_floor']} />

      <GlassCard className="mb-6 fp-rail" data-testid="roi-form">
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="What are you considering?">
            <select value={form.credential_type} onChange={e => setForm({ ...form, credential_type: e.target.value })}
              className="input-dark w-full" data-testid="roi-credential-select">
              {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          {mayAsk('current_income') ? (
            <Field label="Your income today">
              <Input type="number" value={form.current_income} onChange={e => setForm({ ...form, current_income: e.target.value })}
                placeholder="42000" data-testid="roi-income-input" />
            </Field>
          ) : (
            <Field label={askInstead('current_income') || 'The income you are working toward'}>
              <Input type="number" value={form.current_income} onChange={e => setForm({ ...form, current_income: e.target.value })}
                placeholder="Leave blank if you're not earning yet" data-testid="roi-income-input" />
              <p className="text-[11px] text-slate-300 mt-1">{askReason('current_income')}</p>
            </Field>
          )}
          <Field label={`Total cost (average ${money(chosen?.cost)})`}>
            <Input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })}
              placeholder={String(chosen?.cost ?? '')} data-testid="roi-cost-input" />
          </Field>
          <Field label="Covered by grants or funding">
            <Input type="number" value={form.funding_covers} onChange={e => setForm({ ...form, funding_covers: e.target.value })}
              placeholder="0" data-testid="roi-funding-input" />
          </Field>
          <Field label="Amount you'd borrow">
            <Input type="number" value={form.borrowing} onChange={e => setForm({ ...form, borrowing: e.target.value })}
              placeholder="0" data-testid="roi-borrow-input" />
          </Field>
        </div>
        {chosen?.note && <p className="text-xs text-slate-300 mt-3">{chosen.note}</p>}
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <Button onClick={calculate} disabled={busy} className="btn-primary" data-testid="roi-calculate-btn">
            {busy ? 'Working…' : <><Calculator className="w-4 h-4 mr-2" />Run the numbers</>}
          </Button>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={form.keep_working} data-testid="roi-keep-working"
              onChange={e => setForm({ ...form, keep_working: e.target.checked })} />
            I'd keep working while I study
          </label>
        </div>
      </GlassCard>

      {d && (
        <div className="space-y-6" data-testid="roi-result">
          <GlassCard>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <ScoreRing value={paybackPct} display={payback ? `${payback}` : '-'}
                label={payback ? 'years to pay back' : 'no payback'} sublabel={payback ? 'after you finish' : 'on income alone'}
                palette={d.band === 'strong' || d.band === 'good' ? 'emerald' : 'amber'} testId="roi-ring" />
              <div className="flex-1 w-full">
                <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: band.hue }}>{band.label}</span>
                <h3 className="text-lg md:text-xl font-semibold text-white mt-1">{d.verdict}</h3>
                {d.target?.occupation && (
                  <p className="text-sm text-slate-300 mt-1">
                    {d.target.occupation}{d.target.median ? ` · median ${money(d.target.median)}` : ''}{d.target.field ? ` · ${d.target.field}` : ''}
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-300">Total investment</p>
                    <p className="text-xl font-bold text-white fp-num" data-testid="roi-total">{money(d.investment.total)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-300">Gain per year at destination</p>
                    <p className="text-xl font-bold text-emerald-300 fp-num">{money(d.return.annual_gain)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-300">Break-even</p>
                    <p className="text-xl font-bold text-cyan-300 fp-num">
                      {d.return.break_even_month ? `${d.return.break_even_month} mo` : '-'}
                    </p>
                    <p className="text-[10px] text-slate-300">incl. {d.credential.months}mo of study</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-300">10-year net</p>
                    <p className={`text-xl font-bold fp-num ${d.return.ten_year_net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {money(d.return.ten_year_net)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </GlassCard>

          <div className="grid lg:grid-cols-2 gap-6">
            <GlassCard data-testid="roi-breakdown">
              <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Wallet className="w-4 h-4 text-amber-300" /> What it actually costs you
              </h3>
              {[
                ['Tuition out of pocket', d.investment.tuition_out_of_pocket, '#fbbf24'],
                ['Covered by funding', d.investment.funding_covers, '#34d399'],
                ['Wages given up', d.investment.lost_wages, '#fb923c'],
                ['Loan interest over 10 years', d.investment.loan_interest_total, '#f87171'],
              ].map(([label, val, hue]) => (
                <div key={label} className="flex items-center gap-3 py-2">
                  <span className="flex-1 text-sm text-slate-300">{label}</span>
                  <span className="text-sm font-semibold fp-num" style={{ color: val ? hue : '#C6D4E8' }}>{money(val)}</span>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-3 mt-2 border-t border-slate-700">
                <span className="flex-1 text-sm text-white">Total price of the decision</span>
                <span className="text-base font-bold text-white fp-num">{money(d.investment.total)}</span>
              </div>
            </GlassCard>

            <GlassCard data-testid="roi-reasons">
              <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-cyan-300" /> Why the number says what it says
              </h3>
              <ul className="space-y-2">
                {d.reasons.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-300">
                    <span className="text-cyan-400">•</span>{r}
                  </li>
                ))}
              </ul>
              {d.floor_note && (
                <p className="text-sm mt-4 px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700 text-slate-200">
                  <Clock className="w-3.5 h-3.5 inline mr-1.5 text-cyan-300" />{d.floor_note}
                </p>
              )}
              {d.risk_note && (
                <p className="text-sm mt-3 px-3 py-2 rounded-lg border border-amber-400/40 bg-amber-500/10 text-amber-100">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />{d.risk_note}
                </p>
              )}
            </GlassCard>
          </div>

          <p className="text-xs text-slate-300">{d.disclosure} Sources: {d.sources.join(' · ')}.</p>
        </div>
      )}
    </AppLayout>
  );
}
